import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip } from '@mui/material';

import { UUID } from 'src/utils/types';
import UploadModal from 'src/components/UploadModal';
import ConfirmationModal from 'src/components/ConfirmationModal';
import { useLcaAttachments } from 'src/Lca/hooks';
import { LcaProductAttachmentOutSchema } from 'src/Lca/types';

interface FilesProps {
  onDeleteClick: (fileId: UUID) => void;
  values: LcaProductAttachmentOutSchema[];
}

function Files(props: Readonly<FilesProps>) {
  const { values, onDeleteClick } = props;
  return (
    <Box sx={{ display: 'flex', gap: '24px', flexWrap: 'wrap', mb: 2 }}>
      {values.map((file) => (
        <Chip
          key={file.id}
          label={file.name}
          onDelete={() => onDeleteClick(file.id)}
          onClick={() => window.open(file.url, '_blank')}
        />
      ))}
    </Box>
  );
}

interface Props {
  attachmentType: string;
  productId: number;
}

export default function MultipleFilesUpload(props: Readonly<Props>) {
  const { attachmentType, productId } = props;
  const { t } = useTranslation('lca', { keyPrefix: 'basicInfo' });
  const { _data, _delete, refetch } = useLcaAttachments(attachmentType, {
    product_id: productId,
  });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [confirmFileDelete, setConfirmFileDelete] = useState<
    UUID | undefined
  >();

  return (
    <div>
      {_data && (
        <Files
          values={_data}
          onDeleteClick={(fileId) => setConfirmFileDelete(fileId)}
        />
      )}

      <Button variant="secondary" onClick={() => setUploadOpen(true)}>
        {t(attachmentType)}
      </Button>

      <ConfirmationModal
        open={!!confirmFileDelete}
        onClose={() => setConfirmFileDelete(undefined)}
        onFalse={() => setConfirmFileDelete(undefined)}
        onTrue={() => {
          _delete.mutate(confirmFileDelete as UUID);
          setConfirmFileDelete(undefined);
        }}
      />

      <UploadModal
        url={`/web/lca/upload-${attachmentType}?product_id=${productId}`}
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
